/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "./FaqSections.css";

/* Distance kept between the sticky header and the section scrolled to. */
const SCROLL_OFFSET = 110;
/* A section counts as the current one once its top passes this line. */
const ACTIVE_OFFSET = 140;

/* Answers are nodes so they can carry links, so pull their text out for search. */
function nodeText(node) {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(nodeText).join("");
  }
  if (node.props) {
    return nodeText(node.props.children);
  }
  return "";
}

class FaqSections extends Component {
  constructor(props) {
    super(props);
    this.state = {
      query: "",
      open: {},
      activeId: props.groups.length ? props.groups[0].id : "",
    };
    this.sections = {};
    this.handleSearch = this.handleSearch.bind(this);
    this.setActive = this.setActive.bind(this);
  }

  componentDidMount() {
    window.addEventListener("scroll", this.setActive, { passive: true });
    window.addEventListener("resize", this.setActive);
    this.setActive();
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.setActive);
    window.removeEventListener("resize", this.setActive);
  }

  itemKey(groupId, index) {
    return groupId + "-" + index;
  }

  matches(item, query) {
    if (!query) {
      return true;
    }
    const haystack = (item.q + " " + nodeText(item.a)).toLowerCase();
    return haystack.indexOf(query) > -1;
  }

  /* Groups with their surviving items, keeping each item's original index so
     the open/closed map stays stable while the search narrows the list. */
  visibleGroups() {
    const query = this.state.query.trim().toLowerCase();
    return this.props.groups
      .map((group) => ({
        group: group,
        entries: group.items
          .map((item, index) => ({ item: item, index: index }))
          .filter((entry) => this.matches(entry.item, query)),
      }))
      .filter((visible) => visible.entries.length > 0);
  }

  handleSearch(e) {
    const query = e.target.value;
    const trimmed = query.trim().toLowerCase();
    const open = {};
    if (trimmed) {
      this.props.groups.forEach((group) => {
        group.items.forEach((item, index) => {
          if (this.matches(item, trimmed)) {
            open[this.itemKey(group.id, index)] = true;
          }
        });
      });
    }
    this.setState({ query: query, open: open });
  }

  handleToggle(key, isOpen) {
    this.setState((prev) => {
      const open = Object.assign({}, prev.open);
      open[key] = isOpen;
      return { open: open };
    });
  }

  scrollToSection(e, id) {
    e.preventDefault();
    const target = this.sections[id];
    if (!target) {
      return;
    }
    window.scrollTo({
      top:
        target.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET,
      behavior: "smooth",
    });
  }

  setActive() {
    let best = "";
    let bestTop = -Infinity;
    this.props.groups.forEach((group) => {
      const el = this.sections[group.id];
      if (!el || el.offsetParent === null) {
        return;
      }
      const top = el.getBoundingClientRect().top - ACTIVE_OFFSET;
      if (top <= 0 && top > bestTop) {
        bestTop = top;
        best = group.id;
      }
    });
    if (!best && this.props.groups.length) {
      best = this.props.groups[0].id;
    }
    if (best !== this.state.activeId) {
      this.setState({ activeId: best });
    }
  }

  renderItem(groupId, entry) {
    const key = this.itemKey(groupId, entry.index);
    return (
      <details
        className="Faq-item"
        key={key}
        open={!!this.state.open[key]}
        onToggle={(e) => this.handleToggle(key, e.target.open)}
      >
        <summary className="Faq-q">
          {entry.item.q}
          <span className="Faq-plus" />
        </summary>
        <div className="Faq-a">{entry.item.a}</div>
      </details>
    );
  }

  render() {
    const visible = this.visibleGroups();
    const cta = this.props.cta;

    return (
      <div>
        <div className="Faq-hero">
          <div className="Faq-heroInner">
            <h1 className="Faq-title">{this.props.title}</h1>
            <div className="Faq-sub">{this.props.sub}</div>
            <div className="Faq-searchWrap">
              <input
                className="Faq-search"
                type="search"
                autoComplete="off"
                placeholder={this.props.searchPlaceholder}
                value={this.state.query}
                onChange={this.handleSearch}
              />
            </div>
          </div>
        </div>

        <div className="Faq-body">
          <nav className="Faq-side">
            {this.props.groups.map((group) => (
              <a
                className={
                  "Faq-sideLink" +
                  (group.id === this.state.activeId ? " is-active" : "")
                }
                key={group.id}
                href={"#" + group.id}
                onClick={(e) => this.scrollToSection(e, group.id)}
              >
                {group.title}
              </a>
            ))}
          </nav>

          <div className="Faq-groups">
            {visible.map((v) => (
              <section
                className="Faq-group"
                id={v.group.id}
                key={v.group.id}
                ref={(el) => {
                  this.sections[v.group.id] = el;
                }}
              >
                <h2 className="Faq-groupTitle">{v.group.title}</h2>
                <div className="Faq-groupNote">{v.group.note}</div>
                <div className="Faq-list">
                  {v.entries.map((entry) => this.renderItem(v.group.id, entry))}
                </div>
              </section>
            ))}

            {visible.length === 0 && (
              <div className="Faq-empty">{this.props.emptyText}</div>
            )}
          </div>
        </div>

        <div className="Faq-cta">
          <div className="Faq-ctaCard">
            <div className="Faq-ctaTitle">{cta.title}</div>
            <div className="Faq-ctaTxt">{cta.txt}</div>
            <div className="Faq-ctaBtns">
              <Link className="Faq-btnPrimary" to={cta.to}>
                {cta.primary}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

FaqSections.propTypes = {
  title: PropTypes.string.isRequired,
  sub: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  emptyText: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      note: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          q: PropTypes.string.isRequired,
          a: PropTypes.node.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  cta: PropTypes.shape({
    title: PropTypes.string.isRequired,
    txt: PropTypes.string.isRequired,
    primary: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
  }).isRequired,
};

export default FaqSections;
